// features.ts — web-side feature flags (CTSK-493 Phase 1b → CTSK-494 Phase 1c
// → CTSK-496 Phase 1d).
//
// The browser reads the API's runtime flags from GET /api/v1/meta/features
// (apps/api/src/routes/health.ts) once per page load via `useFeatures()`,
// cached for the life of the session. Flipping DOCUMENT_SEARCH_ENABLED on
// the API + restarting it is picked up on the next reload — no web rebuild,
// no CI run, no image push.
//
// /meta/features is the SOLE flag source as of Phase 1d (CTSK-496): the
// build-time `VITE_DOCUMENT_SEARCH_ENABLED` mirror that Phase 1c kept as a
// one-release fallback is gone, along with its Dockerfile/CI/compose
// plumbing. When the endpoint is unreachable, 404s, or answers with a
// malformed field, flags default hard to OFF (`resolved: false` lets
// consumers that care wait for the real answer instead).

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api.js';

/** Runtime feature flags as the web app consumes them. One boolean per flag. */
export interface Features {
  /** `features.documentSearch.enabled` on the API — design doc §7.2 naming. */
  documentSearch: boolean;
  /** CTSK-498 — `DOCUMENT_GROUPING_ENABLED` on the API (runtime only). */
  documentGrouping: boolean;
}

export interface FeaturesState extends Features {
  /**
   * True once /meta/features has answered (success or failure). While false
   * the values are the hard defaults (all off); consumers that must not
   * flash a wrong state (e.g. a global shortcut) can wait on this.
   */
  resolved: boolean;
}

/** Wire shape of GET /api/v1/meta/features. Fields are validated per-key. */
interface MetaFeaturesEnvelope {
  data?: Partial<Record<keyof Features, unknown>> | null;
}

/**
 * Hard defaults — every flag off. Phase 1d (CTSK-496): this replaced the
 * Vite build-time mirror as the fallback for unreachable/older APIs.
 */
export function defaultFeatures(): Features {
  return { documentSearch: false, documentGrouping: false };
}

/**
 * Merge a /meta/features answer onto the fallback, field by field. A field
 * that isn't a boolean (missing key, `data` is an array because a generic
 * mock answered, older API) keeps the fallback value.
 */
export function mergeFeatures(envelope: unknown, fallback: Features): Features {
  const data = (envelope as MetaFeaturesEnvelope | null | undefined)?.data;
  const src: Partial<Record<keyof Features, unknown>> =
    data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  return {
    documentSearch:
      typeof src.documentSearch === 'boolean' ? src.documentSearch : fallback.documentSearch,
    documentGrouping:
      typeof src.documentGrouping === 'boolean' ? src.documentGrouping : fallback.documentGrouping,
  };
}

export const FEATURES_QUERY_KEY = ['meta', 'features'] as const;

/** queryFn — never throws: any failure resolves to the hard defaults. */
export async function fetchFeatures(signal?: AbortSignal): Promise<Features> {
  const fallback = defaultFeatures();
  try {
    const envelope = await apiFetch<unknown>('/meta/features', { signal });
    return mergeFeatures(envelope, fallback);
  } catch {
    return fallback;
  }
}

/**
 * Runtime feature flags. Fetched on first mount of any consumer, cached
 * forever for this page load (flags don't change mid-session; a reload
 * re-reads them). Safe to call from many components — TanStack dedupes.
 */
export function useFeatures(): FeaturesState {
  const q = useQuery({
    queryKey: FEATURES_QUERY_KEY,
    queryFn: ({ signal }) => fetchFeatures(signal),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const values = q.data ?? defaultFeatures();
  return { ...values, resolved: q.data !== undefined };
}
